import React from 'react';
import cx from 'classnames';

import { IconUpArrow, IconDownArrow } from '@trqk/components';

import { formatDelta, formatDollarsStatement } from '@trqk/utils';

const arrowWidths = {
    sm: 20,
    lg: 25,
};

const arrows = {
    increase: {
        lg: <IconUpArrow width={arrowWidths.lg} fillColor="secondary-500" />,
        sm: <IconUpArrow width={arrowWidths.sm} fillColor="secondary-500" />,
    },
    decrease: {
        lg: <IconDownArrow width={arrowWidths.lg} fillColor="quinary-500" />,
        sm: <IconDownArrow width={arrowWidths.sm} fillColor="quinary-500" />,
    },
};

const textColors = {
    increase: 'text-secondary-500',
    decrease: 'text-quinary-500',
};

const fontSizes = {
    lg: 'text-xl',
    sm: 'text-lg',
};

interface DeltaDisplayProps {
    /**
     * When 'grid', the dollar amount is displayed in a separate column from the percent amount and
     * arrow. When 'flex', the two amounts and the arrow flow together.
     */
    displayMode: 'grid' | 'flex';
    /**
     * A delta of earnings between two statements
     */
    earningsDelta: number;
    /**
     * A delta of earnings between two statements, as a  percentage. Since the user can't tell the
     * difference between a 100% delta and a track that was only in one statement, this is null if
     * the track is missing from one of the statements.
     */
    earningsPercentDelta: Maybe<number>;
    /**
     * Determines the scaling of the JSX elements
     */
    size: 'sm' | 'lg';
}

/**
 * Renders a formatted dollar amount, percentage, and arrow (up for increase, down for decrease).
 * @param {DeltaDisplayProps} props
 */
export const DeltaDisplay = ({
    displayMode,
    earningsDelta,
    earningsPercentDelta,
    size,
}: DeltaDisplayProps): JSX.Element => {
    const isIncrease = earningsDelta > 0;
    const sign = isIncrease ? '+' : '-';
    const earningsDeltaFormatted = formatDollarsStatement(
        Math.abs(earningsDelta),
    );
    const earningsPercentDeltaFormatted =
        earningsPercentDelta === null
            ? null
            : formatDelta({
                  percentDelta: earningsPercentDelta,
                  threshold: 300,
              });
    let arrow = null;
    let nullPercentLabel = null;

    if (earningsPercentDelta === null) {
        nullPercentLabel = (
            <div
                className={cx('flex justify-end items-center', {
                    'pl-3': size === 'lg',
                    'pl-2': size === 'sm',
                })}
            >
                {isIncrease && (
                    <span className="text-sm font-semibold text-secondary-500">
                        New
                    </span>
                )}
                {!isIncrease && (
                    <span className="text-sm font-semibold text-quinary-500">
                        Falloff
                    </span>
                )}
            </div>
        );
    }

    if (isIncrease) {
        if (size === 'lg') {
            arrow = arrows.increase.lg;
        } else if (size === 'sm') {
            arrow = arrows.increase.sm;
        }
    } else if (size === 'lg') {
        arrow = arrows.decrease.lg;
    } else if (size === 'sm') {
        arrow = arrows.decrease.sm;
    }

    return (
        <div
            className={cx({
                'flex items-center': displayMode === 'flex',
                'grid w-full': displayMode === 'grid',
            })}
            style={{
                gridTemplateColumns: '55% 45%',
            }}
        >
            <div className="flex justify-end">
                <span
                    className={cx('font-bold', {
                        [textColors.increase]: isIncrease,
                        [textColors.decrease]: !isIncrease,
                        [fontSizes.sm]: size === 'sm',
                        [fontSizes.lg]: size === 'lg',
                    })}
                >
                    {sign}
                </span>
                <span
                    className={cx('font-semibold', {
                        [fontSizes.sm]: size === 'sm',
                        [fontSizes.lg]: size === 'lg',
                    })}
                >
                    {earningsDeltaFormatted}
                </span>
            </div>
            {earningsPercentDeltaFormatted && (
                <div
                    className={cx('flex justify-end items-center', {
                        'pl-3': size === 'lg',
                        'pl-2': size === 'sm',
                    })}
                >
                    <span
                        className={cx('font-semibold', {
                            [textColors.increase]: isIncrease,
                            [textColors.decrease]: !isIncrease,
                            [fontSizes.sm]: size === 'sm',
                            [fontSizes.lg]: size === 'lg',
                        })}
                    >
                        {earningsPercentDeltaFormatted}
                    </span>
                    {arrow}
                </div>
            )}
            {nullPercentLabel && nullPercentLabel}
        </div>
    );
};
